import React, { useState } from "react";
import { X, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { notifyError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Konfirmasi Soft Delete Tenant.
 * User harus mengetik tenant_slug untuk konfirmasi (mencegah delete tidak sengaja).
 */
export default function DeleteTenantDialog({ tenant, open, onClose, onDeleted }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  if (!open || !tenant) return null;
  const slug = tenant.tenant_slug || "";
  const canDelete = typed === slug;

  const doDelete = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      await api.delete(`/platform/tenants/${tenant.tenant_id}`);
      toast.success(`Tenant "${tenant.tenant_name}" dihapus (soft delete).`);
      onDeleted?.(tenant);
      onClose?.();
    } catch (e) {
      notifyError(e, "Gagal menghapus tenant");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="tenant-delete-dialog"
    >
      <div className="w-full max-w-md bg-[#0F0F17] border border-white/10 rounded-3xl shadow-2xl">
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-rose-500/15 grid place-items-center">
            <AlertTriangle size={16} className="text-rose-400" />
          </div>
          <div className="text-white font-display font-black text-base">Hapus Tenant?</div>
          <button
            onClick={onClose}
            className="ml-auto w-8 h-8 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="delete-dialog-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-white/80 leading-relaxed">
            Tenant <strong className="text-white">{tenant.tenant_name}</strong>{" "}
            (<span className="font-mono text-white/60">{tenant.tenant_code}</span>) akan di-<em>soft delete</em>:
          </p>
          <ul className="text-xs text-white/60 mt-2 space-y-1 list-disc list-inside">
            <li>Status berubah menjadi <strong className="text-rose-400">ARCHIVED</strong></li>
            <li>Tersimpan di database dengan <code className="text-white/80">deleted_at</code></li>
            <li>Bisa dipulihkan / audit di masa depan</li>
            <li>Slug tetap tidak bisa dipakai tenant lain</li>
          </ul>

          <div className="mt-4">
            <label className="block text-[11px] text-white/60 uppercase tracking-wider mb-1.5 font-bold">
              Ketik <code className="font-mono text-rose-400">{slug}</code> untuk konfirmasi
            </label>
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={slug}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 font-mono"
              data-testid="delete-confirm-input"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex items-center gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="delete-dialog-cancel">
            Batal
          </Button>
          <Button
            onClick={doDelete}
            disabled={!canDelete || busy}
            className="bg-rose-500 text-white hover:bg-rose-600 disabled:opacity-40"
            data-testid="delete-dialog-confirm"
          >
            {busy ? <><Loader2 size={14} className="mr-1 animate-spin" /> Menghapus…</> : "Hapus Tenant"}
          </Button>
        </div>
      </div>
    </div>
  );
}
